import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import { IPassword } from "../../interfaces/IPassword";
import { IState } from "../../interfaces/IState";
import { IUser } from "../../interfaces/IUser";
import { IUserType } from "../../interfaces/IUserType";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const User: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut, user } = useAuth();

  const [profileIdentifierOrPath, setProfileIdentifierOrPath] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userTypesOpions, setUserTypesOptions] = useState<IOption[]>([]);
  const [userTypeOption, setUserTypeOption] = useState<IOption | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [addressReference, setAddressReference] = useState("");
  const [association, setAssociation] = useState("");
  const [isProfile] = useState(location.pathname === "/profile" ? true : false);
  const [statesOptions, setStatesOptions] = useState<IOption[]>([]);
  const [stateOption, setStateOption] = useState<IOption | null>(null);

  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const userTypeToOption = (userType: IUserType): IOption => {
    return {
      value: `${userType.id}`,
      description: `${userType.id} - ${userType.description}`,
    };
  };

  const handleSetUserType = (value: string | null) => {
    if (value) {
      const selected = userTypesOpions.find(userType => {
        return userType.value === value;
      });

      if (selected?.value) {
        setUserTypeOption(selected);
      } else {
        setUserTypeOption(null);
      }
    }
  };

  const stateToOption = useCallback(
    (state: IState): IOption => ({
      value: `${state.id}`,
      description: `${state.id} - ${state.name}`,
    }),
    [],
  );

  const handleSetState = (value: string | null) => {
    if (value) {
      const selected = statesOptions.find(s => s.value === value);
      setStateOption(selected || null);
    }
  };

  const handleChangeIsActive = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const handleListUserTypes = useCallback(async () => {
    const queryParams = new URLSearchParams(location.search);
    const userTypeParam = queryParams.get("userType");

    await api
      .get(`/user-types?perPage=10&currentPage=1&orderBy=id&orderDirection=ASC`)
      .then(response => {
        const userTypes: IUserType[] = response.data.data;

        if (userTypes.length > 0) {
          const userTypesOptions = userTypes.map(userTypeToOption);
          setUserTypesOptions(userTypesOptions);

          if (userTypeParam) {
            const selected = userTypesOptions.find(
              option => option.value === userTypeParam,
            );
            if (selected) {
              setUserTypeOption(selected);
            }
          }
        } else {
          setUserTypesOptions([]);
        }
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, signOut, location.search, toastr]);

  const handleListStates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/states");
      const options = data.map(stateToOption);
      setStatesOptions(options);
    } catch (error: any) {
      if (error.status === 401) {
        signOut();
        navigate("/");
      }
      toastr.error(
        error.response?.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [signOut, toastr, navigate, stateToOption]);

  const handleRequestZipCode = useCallback(async () => {
    if (zipCode.length === 9) {
      setLoading(true);
      try {
        const { data } = await api.get(`/cep/${zipCode}`);
        setCountry("Brasil");
        setCity(data.localidade || "");
        setNeighborhood(data.bairro || "");
        setAddress(data.logradouro || "");
        setAddressNumber("");
        setAddressComplement("");
        setAddressReference("");
        const state = statesOptions.find(
          s => s.value.toUpperCase() === (data.uf || "").toUpperCase(),
        );
        setStateOption(state || null);
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [zipCode, statesOptions, navigate, signOut, toastr]);

  const handleListUser = useCallback(async () => {
    const id = isProfile
      ? "profile"
      : location.pathname.replace("/user", "").replace("/", "") || "";

    if (id) {
      setProfileIdentifierOrPath(id);
      setLoading(true);

      await api
        .get(`/users/${id}`)
        .then(response => {
          const user: IUser = response.data;

          setName(user.name ? user.name : "");
          setUsername(user.username ? user.username : "");
          setEmail(user.email ? user.email : "");

          if (user.userType) {
            setUserTypeOption(userTypeToOption(user.userType));
          }

          setIsActive(!!user.isActive);

          setZipCode(user.zipCode || "");
          setCountry(user.country || "");
          if (user.state) {
            setStateOption(stateToOption(user.state));
          }
          setCity(user.city || "");
          setNeighborhood(user.neighborhood || "");
          setAddress(user.address || "");
          setAddressNumber(user.addressNumber || "");
          setAddressComplement(user.addressComplement || "");
          setAddressReference(user.addressReference || "");
          setAssociation(user.association || "");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isProfile, navigate, signOut, toastr, location.pathname, stateToOption]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});

      if (profileIdentifierOrPath) {
        const data: IUser = {
          name,
          username,
          email,
          typeId: Number(userTypeOption?.value),
          isActive,
          zipCode,
          country,
          stateId: stateOption?.value || "",
          city,
          neighborhood,
          address,
          addressNumber,
          addressComplement,
          addressReference,
          association,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required("Nome é obrigatórios"),
          username: Yup.string().required("Usuário é obrigatórios"),
          email: Yup.string().email("Formato inválido"),
          typeId: Yup.number()
            .required("Tipo de usuário é obrigatório")
            .moreThan(0, "Tipo de usuário é obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/users/${profileIdentifierOrPath}`, data)
          .then(async () => {
            await handleListUser();
            toastr.success("Usuário atualizado com sucesso");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: IUser = {
          name,
          username,
          email,
          typeId: userTypeOption?.value ? Number(userTypeOption?.value) : 0,
          password,
          confirmPassword,
          zipCode,
          country,
          stateId: stateOption?.value || "",
          city,
          neighborhood,
          address,
          addressNumber,
          addressComplement,
          addressReference,
          association,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required("Nome é obrigatórios"),
          username: Yup.string().required("Nome de usuário é obrigatórios"),
          email: Yup.string().email("Formato inválido"),
          typeId: Yup.number()
            .required("Tipo de usuário é obrigatório")
            .moreThan(0, "Tipo de usuário é obrigatório"),
          password:
            userTypeOption?.value !== "3"
              ? Yup.string().required("Senha é obrigatória")
              : Yup.string().nullable().optional(),
          confirmPassword:
            userTypeOption?.value !== "3"
              ? Yup.string()
                  .required("Confirmação de senha é obrigatória")
                  .oneOf([Yup.ref("password")], "As senhas não conferem")
              : Yup.string().nullable().optional(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/users`, data)
          .then(() => {
            toastr.success("Usuário cadastrado com sucesso");
            navigate("/users");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(error?.message || "Contate a equipe de suporte");
    } finally {
      setLoading(false);
    }
  }, [
    profileIdentifierOrPath,
    name,
    username,
    email,
    password,
    confirmPassword,
    userTypeOption,
    isActive,
    zipCode,
    country,
    stateOption,
    city,
    neighborhood,
    address,
    addressNumber,
    addressComplement,
    addressReference,
    association,
    navigate,
    signOut,
    toastr,
    handleListUser,
  ]);

  const handleUpdatePassword = useCallback(async () => {
    try {
      if (profileIdentifierOrPath) {
        setFormErrors({});
        const data: IPassword = {
          password,
          confirmPassword,
        };

        const schema = Yup.object().shape({
          password: Yup.string().required("Senha é obrigatória"),
          confirmPassword: Yup.string()
            .required("Confirmação de senha é obrigatória")
            .oneOf([Yup.ref("password")], "As senhas não conferem"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/users/${profileIdentifierOrPath}/password`, data)
          .then(() => {
            toastr.success("Senha atualizada com sucesso");
            navigate(`/users`);
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        toastr.error("Usuário não selecionado. Contate a equipe de suporte");
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(error?.message || "Contate a equipe de suporte");
    } finally {
      setLoading(false);
    }
  }, [
    profileIdentifierOrPath,
    password,
    confirmPassword,
    navigate,
    signOut,
    toastr,
  ]);

  useEffect(() => {
    Promise.all([handleListStates(), handleListUserTypes(), handleListUser()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        {isProfile ? <h1>Meu Perfil</h1> : <h1>Usuário</h1>}
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                autoFocus
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Usuário"
                value={username}
                onChange={e =>
                  setUsername(String(e.target.value).toLowerCase().trim())
                }
                error={!!formErrors.username}
                helperText={formErrors.username}
                required
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="E-mail"
                value={email}
                onChange={e => setEmail(e.target.value.toLowerCase().trim())}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>

            {user.typeId === 1 && (
              <Grid item xs={12} md={6} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formErrors.typeId}
                  required
                >
                  <InputLabel id="select-filter" error={!!formErrors.typeId}>
                    Tipo de usuário
                  </InputLabel>
                  <Select
                    label="Tipo de usuário"
                    value={userTypeOption?.value || ""}
                    onChange={e => handleSetUserType(String(e.target.value))}
                    error={!!formErrors.typeId}
                    required
                  >
                    {userTypesOpions.map(situation => {
                      return (
                        <MenuItem
                          key={situation.description}
                          value={situation.value}
                        >
                          {situation.description}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>{formErrors.typeId}</FormHelperText>
                </FormControl>
              </Grid>
            )}

            {!profileIdentifierOrPath && userTypeOption?.value !== "3" && (
              <>
                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    value={password}
                    onChange={e => setPassword(e.target.value.trim())}
                    type="password"
                    autoComplete="off"
                    helperText={formErrors.password}
                    error={!!formErrors.password}
                    id="password"
                    label="Senha"
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value.trim())}
                    type="password"
                    autoComplete="off"
                    helperText={formErrors.confirmPassword}
                    error={!!formErrors.confirmPassword}
                    id="confirmPassword"
                    label="Confirmar senha"
                  />
                </Grid>
              </>
            )}
          </Grid>

          {userTypeOption?.value === "3" && (
            <>
              <Divider />
              <Typography
                variant="h6"
                gutterBottom
                sx={{ marginTop: "24px", marginBottom: "24px" }}
              >
                Dados do colecionador
              </Typography>
              <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
                <Grid item xs={12} md={6} sm={6}>
                  <InputMask
                    mask={"99999-999"}
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value)}
                    maskPlaceholder={null}
                    onBlur={handleRequestZipCode}
                  >
                    <TextField
                      sx={{ width: "100%" }}
                      size="small"
                      label="CEP"
                      helperText={formErrors.zipCode}
                      error={!!formErrors.zipCode}
                    />
                  </InputMask>
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="País"
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!formErrors.stateId}
                  >
                    <InputLabel>Estado</InputLabel>
                    <Select
                      label="Estado"
                      value={stateOption?.value || ""}
                      onChange={e => handleSetState(String(e.target.value))}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 5.5, // 5 itens visíveis (aprox. 48px cada)
                          },
                        },
                      }}
                    >
                      {statesOptions.map(s => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.description}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formErrors.stateId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Cidade"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    error={!!formErrors.city}
                    helperText={formErrors.city}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Bairro"
                    value={neighborhood}
                    onChange={e => setNeighborhood(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Endereço"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Número"
                    value={addressNumber}
                    onChange={e => setAddressNumber(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Complemento"
                    value={addressComplement}
                    onChange={e => setAddressComplement(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Referência"
                    value={addressReference}
                    onChange={e => setAddressReference(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6} sm={6}>
                  <TextField
                    sx={{ width: "100%" }}
                    size="small"
                    label="Associação"
                    value={association}
                    onChange={e => setAssociation(e.target.value)}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6} sm={6}>
            <FormControlLabel
              label="Ativo"
              control={
                <Checkbox checked={isActive} onChange={handleChangeIsActive} />
              }
            />
          </Grid>

          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={
                isProfile ? () => navigate(-1) : () => navigate("/users")
              }
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        {loading && <BackdropCustom />}
      </PageCard>

      {profileIdentifierOrPath && userTypeOption?.value !== "3" && (
        <PageCard>
          <Form>
            <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
              <Grid item xs={12} md={6} sm={6}>
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  value={password}
                  onChange={e => setPassword(e.target.value.trim())}
                  type="password"
                  autoComplete="off"
                  helperText={formErrors.password}
                  error={!!formErrors.password}
                  id="passwordUpdate"
                  label="Senha"
                />
              </Grid>

              <Grid item xs={12} md={6} sm={6}>
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value.trim())}
                  type="password"
                  autoComplete="off"
                  helperText={formErrors.confirmPassword}
                  error={!!formErrors.confirmPassword}
                  id="confirmPasswordUpdate"
                  label="Confirmar senha"
                />
              </Grid>
            </Grid>

            <ButtonGroup justformobilie>
              <ButtonTheme
                onClick={handleUpdatePassword}
                variant="contained"
                disabled={loading}
              >
                Alterar senha
              </ButtonTheme>
            </ButtonGroup>
          </Form>
        </PageCard>
      )}
    </>
  );
};
